import { useCallback, useEffect, useMemo } from 'react';

import { Tags } from '@/entities/vfo/types';
// import { Member, UserStatus } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import {
  ApiCreateCaseDto,
  ApiUpdateCaseDto,
  ApiUpdateCaseMemberSelfDtoV2,
} from '@/shared/api/generated/data-contracts';
import { useApi } from '@/shared/api/hooks/useApi';

import { useCasesStore } from '../stores/cases.store';

import { useCaseSocketEvents } from './useCaseSocketEvents';
import { useCaseTitle } from './useCaseTitle';
import { useMembers } from './useMembers';

type AddMemberParams = {
  caseId: string;
  profileId: number;
  tag: Tags;
  country?: ApiUpdateCaseMemberSelfDtoV2['country'];
};

export const useCases = () => {
  const api = useApi();
  const notices = useNotices();

  useCaseSocketEvents();

  const {
    cases: casesStore,
    isLoading,
    setCases,
    addCase,
    updateCase,
    removeCase: removeCaseFromStore,
    setLoading,
    setInitialized,
    isInitialized: isInitializedGlobal,
  } = useCasesStore();
  const { fetchMembers, members } = useMembers();
  const getCaseTitle = useCaseTitle();

  const fetchCases = useCallback(async () => {
    if (isLoading) return;
    setLoading(true);

    const response = await api.casesGetAll();

    if (response.error) {
      notices.error('Failed to load cases');
      setLoading(false);
      setInitialized(true);
      return;
    }

    setCases(response.data ?? []);
    setLoading(false);
    setInitialized(true);
  }, [api, notices, setCases, setLoading, setInitialized]);

  const cases = useMemo(() => {
    if (members.length === 0) return casesStore;
    return casesStore.map((item) => ({
      ...item,
      title: getCaseTitle(item),
    }));
  }, [members, casesStore]);

  const createCase = useCallback(
    async (data: ApiCreateCaseDto) => {
      const response = await api.casesCreate(data);
      if (response.error) {
        notices.error('Failed to create case');
        return null;
      }

      if (response.data) {
        addCase(response.data);
      }

      return response.data;
    },
    [api, notices, addCase],
  );

  const addMember = useCallback(
    async ({ caseId, profileId, tag, country }: AddMemberParams) => {
      if (!tag) return false;
      const response = await api.casesAddMember(caseId, { profileId, tag, country });

      if (response.error) {
        notices.error('Failed to add member');
        return null;
      }

      const caseResponse = await api.casesGetOne(caseId);
      if (caseResponse.error) {
        notices.error('Failed to refresh case data');
        return null;
      }

      if (caseResponse.data) {
        updateCase(caseId, caseResponse.data);
      }

      return response.data;
    },
    [api, notices, updateCase, fetchMembers],
  );

  const removeMember = useCallback(
    async (caseId: string, profileId: number): Promise<boolean> => {
      const response = await api.casesRemoveMember(caseId, { profileId });

      if (response.error) {
        notices.error('Failed to remove member');
        return false;
      }

      const caseResponse = await api.casesGetOne(caseId);
      if (caseResponse.error) {
        notices.error('Failed to refresh case data');
        return false;
      }

      if (caseResponse.data) {
        updateCase(caseId, caseResponse.data);
      }

      return true;
    },
    [api, notices, updateCase, fetchMembers],
  );

  const findCase = useCallback(
    (caseId?: string) => {
      return cases.find((item) => item.id === caseId);
    },
    [cases],
  );

  const getMemberCases = useCallback(
    (profileId: number) => {
      return cases.filter(
        (caseData) =>
          caseData.profileId === profileId ||
          caseData.members.some((member) => member.profileId === profileId),
      );
    },
    [cases],
  );

  const getCaseMemberById = useCallback(
    (caseId: string, profileId: number) => {
      return cases
        .find((item) => item.id === caseId)
        ?.members.find((member) => member.profileId === profileId);
    },
    [cases],
  );

  const getDocumentCases = useCallback(
    (documentId: string) => {
      return cases.filter((caseData) =>
        caseData.caseDocuments.some((document) => document.documentId === documentId),
      );
    },
    [cases],
  );

  const updateCaseDetails = useCallback(
    async (caseId: string, data: ApiUpdateCaseDto): Promise<boolean> => {
      const response = await api.casesUpdate(caseId, data);

      if (response.error) {
        notices.error('Failed to update case');
        return false;
      }

      if (response.data) {
        updateCase(caseId, response.data);
      }

      return true;
    },
    [api, notices, updateCase],
  );

  const deleteCase = useCallback(
    async (caseId: string): Promise<boolean> => {
      const response = await api.casesDelete(caseId);
      if (response.error) {
        notices.error('Failed to delete case');
        return false;
      }

      removeCaseFromStore(caseId);
      return true;
    },
    [api, notices, removeCaseFromStore],
  );

  const attachDocument = useCallback(
    async (caseId: string, documentId: string): Promise<boolean> => {
      const response = await api.documentsAddDocuments(documentId, { caseId: caseId });

      if (response.error) {
        notices.error('Failed to add documents');
        return false;
      }

      const caseResponse = await api.casesGetOne(caseId);
      if (caseResponse.error) {
        notices.error('Failed to refresh case data');
        return false;
      }

      if (caseResponse.data) {
        updateCase(caseId, caseResponse.data);
      }

      return true;
    },
    [api, notices, updateCase],
  );

  const removeDocument = useCallback(
    async (caseId: string, documentId: string): Promise<boolean> => {
      const response = await api.documentsRemoveFromCase(documentId, { caseId: caseId });

      if (response.error) {
        notices.error('Failed to remove documents');
        return false;
      }

      const caseResponse = await api.casesGetOne(caseId);
      if (caseResponse.error) {
        notices.error('Failed to refresh case data');
        return false;
      }

      if (caseResponse.data) {
        updateCase(caseId, caseResponse.data);
      }

      return true;
    },
    [api, notices, updateCase],
  );

  const findByChatId = useCallback(
    (chatId: string) => {
      return cases.find((caseData) => caseData.chatId === chatId);
    },
    [cases],
  );

  useEffect(() => {
    // get the live store state to avoid race conditions
    const { isInitialized, isLoading: isLoadingLive } = useCasesStore.getState();
    if (!isInitialized && !isLoadingLive) {
      fetchCases();
    }
  }, [fetchCases, isInitializedGlobal]);

  return useMemo(
    () => ({
      cases,
      isLoading,
      findCase,
      createCase,
      updateCase: updateCaseDetails,
      deleteCase,
      fetchCases,
      findByChatId,
      getCaseMemberById,
      getMemberCases,
      addMember,
      removeMember,
      getDocumentCases,
      attachDocument,
      removeDocument,
    }),
    [
      cases,
      isLoading,
      findCase,
      createCase,
      updateCaseDetails,
      deleteCase,
      fetchCases,
      findByChatId,
      getCaseMemberById,
      getMemberCases,
      addMember,
      removeMember,
      getDocumentCases,
      attachDocument,
      removeDocument,
    ],
  );
};
